import classNames from "classnames";
import { Entry } from "contentful";
import Link from "next/link";
import { AbstractIntlMessages, useTranslations } from "next-intl";
import { useEffect } from "react";

import IconAccount from "@/icons/icon-account";
import IconCart from "@/icons/icon-cart";
import IconClose from "@/icons/icon-close";
import IconHeart from "@/icons/icon-heart";
import IconKiko from "@/icons/icon-kiko";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useCartNotification } from "~/contexts/cart-notification";
import { useWishlist } from "~/contexts/wishlist";
import { useCart } from "~/hooks/use-cart";
import useSubscriptions from "~/hooks/use-subscriptions";
import { LoginComponentContext } from "~/shared/user/login-form";
import Editorials from "~/types/editorials";
import appRoutes from "~/utils/app-routes";
import Constants from "~/utils/constants";
import { assertEditorialType } from "~/utils/editorial-utils";
import useScrollDirection from "~/utils/use-scroll-direction";

import Container from "../common/container";
import Icon from "../common/icon";
import { PagePopup, PagePopupClose, PagePopupContent, PagePopupTrigger } from "../common/page-popup";
import ModalGuest from "../login/modal-guest";
import stylesPagePopupGuest from "../login/modal-guest.module.scss";
import MiniCart from "../mini-cart/mini-cart";
import styles from "./header.module.scss";
import MenuMobile from "./menu-mobile";
import Navigation from "./navigation";
import SeoNavigation from "./seo-navigation";
import TopBar from "./top-bar/top-bar";
type Props = {
  entry: Entry<unknown>;
  navColor: Editorials.navColor;
  inPpd?: boolean;
  searchSuggestions?: string[];
  messages?: AbstractIntlMessages;
  isHomepage?: boolean;
};

export default function Header(props: Props) {
  const t = useTranslations();
  const { isHovering, setHovering } = useCartNotification();
  const { products } = useWishlist();

  const cart = useCart();
  const shouldShowCart = !!cart && !cart.ShoppingCart?.IsPaid;

  const breakpoint = useBreakpoint();
  const otherScrollDetector = useScrollDirection({});
  const { dir, y } = useScrollDirection({
    initialDirection: "up",
    thresholdPixels: breakpoint == "desktop" ? Constants.HEADER_HEIGHT_DESKTOP : Constants.HEADER_HEIGHT_MOBILE,
  });

  const { isAuthenticated, user } = useAuthContext();
  const { isUserSubscribedToLoyalty, loyaltyPoints, currentLoyaltyTier, loyaltyTiers } = useSubscriptions();

  useEffect(() => {
    if (y >= Constants.HEADER_HEIGHT_DESKTOP) {
      document.body.style.setProperty("--top-menu", "80px");
      document.body.style.setProperty("--header-menu", "0");
    } else {
      document.body.style.setProperty("--top-menu", `${Constants.HEADER_HEIGHT_DESKTOP}px`);
      document.body.style.setProperty("--header-menu", "80px");
    }
  }, [y]);

  function onOpenMinicartChange(open: boolean) {
    if (open) {
      setHovering(true);
    } else {
      setTimeout(() => setHovering(false), 500);
    }
  }

  const cartLink = (
    <Link className={styles.headerElement} href={appRoutes.CART} tabIndex={-1} aria-hidden={true}>
      <Icon icon={IconCart} value={shouldShowCart ? cart.ShoppingCart?.TotalItems || undefined : undefined} />
    </Link>
  );

  <Icon icon={IconCart} />;
  assertEditorialType<Editorials.Nav>(props.entry, ["navigation"]);

  return (
    <header
      id="header"
      className={classNames(
        styles.header,
        { [styles.scrolledTotal]: dir == "down" },
        { [styles.scrolledTop]: y >= Constants.HEADER_HEIGHT_DESKTOP },
        { [styles.scrolledNavBg]: otherScrollDetector.y >= 1 },
        { [styles.inPdp]: props.inPpd },
        styles[props.navColor]
      )}
    >
      <TopBar entry={props.entry.fields.topBar} />
      <div className={styles.main}>
        <Container className={styles.mainContainer}>
          {/* which contains search */}
          <MenuMobile nav={props.entry} messages={props.messages} searchSuggestions={props.searchSuggestions} />
          <div className={styles.logoWrapper}>
            <Link href="/" className={styles.center} prefetch={false}>
              <Icon icon={IconKiko} aria-hidden className={styles.logoIcon} />
              {props.isHomepage && (
                <h1 className="sr-only">
                  <span>KIKO</span>
                </h1>
              )}
            </Link>
          </div>

          {/* User popover (only on desktop) */}
          {breakpoint === "desktop" ? (
            isAuthenticated ? (
              <div className={styles.myAccountContainer}>
                {isUserSubscribedToLoyalty && (
                  <div className={styles.helloContainer}>
                    <p className={styles.helloText}>
                      {t("generic.hello_user", { name: user?.FirstName }).toUpperCase()}
                    </p>
                    <span className={styles.loyaltyContainer}>
                      <p className={styles.loyaltyTitle}>{currentLoyaltyTier?.Name || loyaltyTiers[0].description}</p>
                      <p className={styles.loyaltyPoints}>{loyaltyPoints ?? ""} pt</p>
                    </span>
                  </div>
                )}
                <Link prefetch={false} href={appRoutes.MYACCOUNT} className={styles.headerElement}>
                  <Icon icon={IconAccount} value={user?.FullName?.[0].toUpperCase()} />
                </Link>
              </div>
            ) : (
              <PagePopup closesOn="click" opensOnSR={true}>
                {/* Guest user PagePopup */}
                <PagePopupTrigger aria-label={t("generic.account.toggle")}>
                  <Link href={appRoutes.LOGIN} className={styles.headerElement} tabIndex={-1} aria-hidden={true}>
                    <Icon icon={IconAccount} />
                  </Link>
                </PagePopupTrigger>
                <PagePopupContent>
                  <PagePopupClose className={stylesPagePopupGuest.closeButton}>
                    <Icon icon={IconClose} width={16} height={16} />
                  </PagePopupClose>
                  <ModalGuest context={LoginComponentContext.headerModal} />
                </PagePopupContent>
              </PagePopup>
            )
          ) : (
            <Link
              prefetch={false}
              href={`${isAuthenticated ? appRoutes.MYACCOUNT : appRoutes.LOGIN}`}
              className={styles.headerElement}
            >
              <Icon
                icon={IconAccount}
                value={isAuthenticated ? user?.FullName?.[0].toUpperCase() ?? undefined : undefined}
              />
            </Link>
          )}
          {/* {breakpoint == "desktop" && ( */}
          <Link
            prefetch={false}
            href={isAuthenticated ? appRoutes.MYACCOUNT_WISHLIST : appRoutes.LOGIN}
            className={styles.headerElement}
            data-desktop-only
            aria-label={t("a11y.goToWishlist")}
          >
            <Icon icon={IconHeart} value={products?.length || undefined} aria-hidden="true" />
          </Link>
          {/* )} */}
          <PagePopup open={isHovering} onOpenChange={onOpenMinicartChange} opensOnSR={true}>
            {breakpoint == "desktop" ? (
              <PagePopupTrigger aria-label={t("generic.cart.toggle")}>{cartLink}</PagePopupTrigger>
            ) : (
              cartLink
            )}
            <MiniCart />
          </PagePopup>
        </Container>
      </div>
      <Navigation nav={props.entry} className={styles.headerNav} />
      <SeoNavigation nav={props.entry} />
    </header>
  );
}
